import React, { useState, useEffect, useMemo } from "react";
import { useLoaderData, useRevalidator, useRouteLoaderData } from "react-router-dom";
import { Collapse, Button, Col, Container, Form, Row, ButtonGroup, Stack, InputGroup, Card } from "react-bootstrap";
import { toast } from "react-toastify";

import { axiosInstance } from "../auth/Auth";
import LoadingIndicator from "../components/LoadingIndicator";
import { PencilFill, TrashFill, XLg, CheckLg, Search } from "react-bootstrap-icons";
import StationPicker from "../components/Pickers/StationPicker";
import MarketPicker from "../components/Pickers/MarketPicker";
import MinMaxSpinsPicker from "../components/Pickers/MinMaxSpinsPicker";
import WeeksPicker from "../components/Pickers/WeeksPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import FrequencyPicker from "../components/Pickers/FrequencyPicker";
import EmailAddressPicker from "../components/Pickers/EmailAddressPicker";
import MusicSelectionTypePicker from "../components/Pickers/MusicSelectionTypePicker";
import ConfirmPrompt from "../components/ConfirmPrompt";
import useSortableData from "../components/useSortableData";
import PageDesciption from "../components/PageDesciption";
import { useUserFormState } from "../components/UserSelection";
import moment from "moment";
import { useLatestCalculatedDate, useSetDaypartOnChange, useSetDaysOnPerspectiveChange, useSetPerspectiveOnDaysChange } from "../components/HelperHooks";
import { daysPerspectives } from "../components/constants";

const extraDefaults = { selectionType: "all", minSpins: 0, maxSpins: 0 };

export default function MusicReports() {
    const moduleName = "musicReports";
    const [errorCode, setErrorCode] = useState(null);
    const { radios, userInfo, competitorRadios, competitorMarkets, radioTags, radiosModuleSettings } = useRouteLoaderData("root");
    const { reports } = useLoaderData();
    const moduleId = useMemo(() => ({ 0: 38, 1: 38, 2: 38 }), []);
    const [reportsData, setReportsData] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [deleteReportId, setDeleteReportId] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [formData, setFormData] = useUserFormState(moduleName, extraDefaults);
    const [allRadios, setAllRadios] = useState([]);
    const [competitorMarketsMap, setCompetitorMarketsMap] = useState({});
    const [radioTagsMap, setRadioTagsMap] = useState({});

    useEffect(() => {
        Promise.all([radios, competitorRadios, radiosModuleSettings, competitorMarkets, radioTags]).then(
            ([resolvedRadios, resolvedCompetitorRadios, resolvedModuleSettings, resolvedCompetitorMarkets, resolvedRadioTags]) => {
                const mainRadios = resolvedRadios?.data?.map(r => ({ radio_id: r.radio_id, radio_name: r.radio_name })) ?? [];
                const competitors = resolvedCompetitorRadios?.data?.map(r => ({ radio_id: r.radio_id, radio_name: r.radio_name })) ?? [];
                setAllRadios([...mainRadios, ...competitors]);

                const marketsMap = {};
                resolvedCompetitorMarkets?.data?.forEach(m => {
                    marketsMap[m.competitor_market_id] = m.radio.radio_name;
                });
                setCompetitorMarketsMap(marketsMap);

                const tagsMap = {};
                resolvedRadioTags?.data?.forEach(t => {
                    tagsMap[t.id] = t.name;
                });
                setRadioTagsMap(tagsMap);
            }
        );
    }, [radios, competitorRadios, radiosModuleSettings, competitorMarkets, radioTags]);
    const revalidator = useRevalidator();

    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    useEffect(() => {
        async function setData() {
            setReportsData((await reports).data);
        }
        setData();
    }, [radios, reports]);

    useEffect(() => {
        async function getEmailAddress() {
            const userInfoData = (await userInfo).data;
            if (userInfoData.email) {
                setFormData(current => ({
                    ...current,
                    email_address: [{ email: userInfoData.email }],
                }));
            }
        }
        getEmailAddress();
    }, [userInfo]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        const resolvedUser = (await userInfo).data;
        const startTime = moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss");
        const endTime = moment()
            .hours(formData.hourRange[1] - 1)
            .minutes(59)
            .seconds(59)
            .format("HH:mm:ss");

        const marketTypeQuery = formData.market.length ? formData.market[0].type : "radio";

        const input = {
            user_id: resolvedUser.user_id,
            market_id: formData.station[0]?.radio_id ?? formData.market[0].market_id,
            market_type: marketTypeQuery,
            daypart_id: formData.daypart?.daypart_id ?? -1,
            days: formData.days,
            frequency: formData.run_frequency,
            weeks_to_check: formData.number_weeks,
            min_spins: formData.minSpins,
            max_spins: formData.maxSpins,
            start_time: startTime,
            end_time: endTime,
            new: formData.selectionType === "new" ? true : false,
            limit: formData.selectionType === "top" ? 50 : 0,
            email_address: formData.email_address?.map(x => x.email) ?? [],
        };

        try {
            const response = await axiosInstance.post("reports/music/", input);
            revalidator.revalidate();
            setFormData({
                ...formData,
                email_address: resolvedUser.email ? [{ email: resolvedUser.email }] : [],
            });
            setErrorCode(null);
            toast.success(`Music Report has been created.`, { autoClose: 5000 });
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
            toast.error(`Error trying to create a Music Report.`);
        }
    }
    function mapReportToEdit(report) {
        return {
            ...report,
            hourRange: [moment(report.start_time, "HH:mm:ss").hours(), moment(report.end_time, "HH:mm:ss").hours() + 1],
            minSpins: report.min_spins ?? 0,
            maxSpins: report.max_spins ?? 0,
            run_frequency: report.frequency ?? 0,
            number_weeks: report.weeks_to_check ?? 0,
            email_address: (report.email_address || []).map(e => (typeof e === "string" ? { email: e } : e)),
        };
    }

    async function handleSaveClick(report) {
        const input = {
            user_id: report.user_id,
            market_id: report.market_id ?? -1,
            market_type: report.market_type,
            report_type: report.report_type,
            frequency: report.run_frequency,
            weeks_to_check: report.number_weeks,
            min_spins: report.minSpins,
            max_spins: report.maxSpins,
            email_address: report.email_address?.map(x => x.email) ?? [],
            start_time: moment().hours(report.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss"),
            end_time: moment()
                .hours(report.hourRange[1] - 1)
                .minutes(59)
                .seconds(59)
                .format("HH:mm:ss"),
            new: report.new,
            days: report.days,
        };

        try {
            await axiosInstance.put("/reports/music/" + report.report_id, input);
            setSelectedReport(null);
            revalidator.revalidate();
            toast.success("Report updated successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Error updating report.");
        }
    }

    async function deleteMusicReport(id) {
        try {
            const response = await axiosInstance.delete("/reports/music/" + id);
            revalidator.revalidate();
            setDeleteReportId(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    const musicReportsMemo = useMemo(() => {
        if (!reportsData) return [];

        return reportsData.filter(x => {
            const marketId = x.market_id?.toString() || "";
            const emails = Array.isArray(x.email_address) ? x.email_address.join(",") : x.email_address || "";
            const minSpins = x.min_spins?.toString() || "";
            const maxSpins = x.max_spins?.toString() || "";
            const frequency = x.frequency?.toString() || "";
            const weeks = x.weeks_to_check?.toString() || "";

            const search = searchValue.toLowerCase().trim();
            const marketDisplayName = getMarketDisplayName(x).toLowerCase();

            return (
                marketId.includes(search) ||
                marketDisplayName.includes(search) ||
                emails.toLowerCase().includes(search) ||
                minSpins.includes(search) ||
                maxSpins.includes(search) ||
                frequency.includes(search) ||
                weeks.includes(search)
            );
        });
    }, [reportsData, searchValue, allRadios, competitorMarketsMap, radioTagsMap]);

    function getMarketDisplayName(report) {
        if (report.market_type === "radio") {
            const radio = allRadios.find(r => r.radio_id === report.market_id);
            return radio?.radio_name ?? "Unknown";
        }
        if (report.market_type === "competitor") {
            return "Competitor market for " + (competitorMarketsMap[report.market_id] ?? "Unknown");
        }
        if (report.market_type === "airplay") {
            const radio = allRadios.find(r => r.radio_id === report.market_id);
            return "Airplay market for " + (radio?.radio_name ?? "Unknown");
        }
        if (report.market_type === "tag") {
            return radioTagsMap[report.market_id] ?? "Unknown tag";
        }
        return "Unknown";
    }

    const { sortedItems, requestSort, sortConfig } = useSortableData(musicReportsMemo);

    return (
        <Container fluid className="p-4 ">
            <Row>
                <Col className="bg-white p-4">
                    <Form className="row" onSubmit={handleFormSubmit}>
                        <Col xs={12} xl className="order-xl-last mb-xl-0 ">
                            <h1 className="text-uppercase">Music Reports</h1>
                            <div className="d-flex flex-column justify-content-between align-items-xl-start">
                                <p className="ps-1">See your reports below, or set up a new one with a click.</p>
                                <Button
                                    type="button"
                                    onClick={() => setAdvancedOpen(!advancedOpen)}
                                    className="btn-ra-yellow text-uppercase px-4 py-xl-1"
                                    aria-controls="music-test-advanced"
                                    aria-expanded={advancedOpen}>
                                    SET UP NEW
                                </Button>
                            </div>
                            <Collapse in={advancedOpen}>
                                <Row className="gy-3">
                                    <Col xs={12} xl={10}>
                                        <Row className="pt-4 gy-3" id="music-test-advanced ">
                                            <StationPicker
                                                formData={formData}
                                                setFormData={setFormData}
                                                name={moduleName}
                                                disabled={formData.market.length}
                                                includeCompetitors
                                            />
                                            <MarketPicker formData={formData} setFormData={setFormData} name={moduleName} disabled={formData.station.length} />
                                            {/* <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} /> */}
                                            {/* <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} /> */}
                                            <MusicSelectionTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                                        </Row>
                                        <Row className="my-2 pb-3 gy-3">
                                            <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <FrequencyPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <WeeksPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />{" "}
                                        </Row>
                                        <EmailAddressPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                    </Col>
                                    <Col xs={12} xl={2} className="text-center text-xl-end align-self-end ms-auto">
                                        <Button
                                            type="submit"
                                            className="btn-ra-yellow text-uppercase px-5"
                                            disabled={(!formData.station.length && !formData.market.length) || !formData.email_address.length}>
                                            SET UP
                                        </Button>
                                    </Col>
                                </Row>
                            </Collapse>
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr className="m-2"></hr>
            <Row>
                <Col className="bg-white p-4">
                    {!reportsData?.length > 0 && !errorCode && (
                        <PageDesciption
                            title={"Music Reports"}
                            whatList={[
                                "Get any number of music reports or charts from the Pro Suite delivered to your mail inbox automatically",
                                "Choose any station or market, and whether you want to see new songs, top50, or all songs played",
                                "Set the amount of data you want, the number of minim spins, and how often you would like to receive the report",
                            ]}
                            knowList={[
                                { text: "Downloads available as Excel, CSV, or HTML" },
                                { text: "You can edit or remove the parametres by the edit button" },
                                { text: "Get the cool card view or the compact list view depending on your preferences" },
                            ]}
                            relatedLinks={[
                                {
                                    title: "Automate Your Meeting Prep",
                                    url: "https://radioanalyzer.com/blog/automate-your-meeting-prep",
                                },
                            ]}
                            videoSrc="https://www.youtube.com/embed/7mUiJxilotU?si=aFNxzQt6caQyCFX1"
                        />
                    )}

                    {!reportsData ? (
                        <span colSpan={12}>
                            <LoadingIndicator />
                        </span>
                    ) : (
                        reportsData.length > 0 && (
                            <>
                                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                                    <p>Here you'll find an overview of your music reports. Add new music reports on the top.</p>
                                </Stack>
                                <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
                                    <Col xs={12} xl={8}>
                                        <InputGroup>
                                            <InputGroup.Text className="input-icon">
                                                <Search />
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={searchValue}
                                                onChange={e => {
                                                    setSearchValue(e.target.value);
                                                }}
                                                placeholder="search by radio name, spins or email address"
                                            />
                                        </InputGroup>
                                    </Col>
                                </Stack>

                                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-3">
                                    {sortedItems.length ? (
                                        sortedItems.map((report, index) => (
                                            <div key={report.report_id} className="col mb-4">
                                                <Card className="w-100 ">
                                                    <Card.Header className="bg-ra-dark-blue text-light d-flex justify-content-between align-items-center text-center fw-bold">
                                                        <div className="d-flex justify-content-center align-items-center fw-bold fs-6 text flex-grow-1">
                                                            {getMarketDisplayName(report)}{" "}
                                                            {report.new === true ? "(New Songs)" : !report.limit ? "(All Songs)" : "(Top 50)"}
                                                        </div>
                                                        {selectedReport?.report_id === report.report_id ? (
                                                            <ButtonGroup size="sm" className="pe-1">
                                                                <Button
                                                                    variant="success"
                                                                    title="Save"
                                                                    disabled={!selectedReport?.email_address.length}
                                                                    onClick={() => handleSaveClick(selectedReport)}>
                                                                    <CheckLg />
                                                                </Button>
                                                                <Button variant="danger" title="Discard" onClick={() => setSelectedReport(null)}>
                                                                    <XLg />
                                                                </Button>
                                                            </ButtonGroup>
                                                        ) : (
                                                            <ButtonGroup size="sm" className="pe-1">
                                                                <Button
                                                                    variant="primary"
                                                                    title="Edit"
                                                                    onClick={() => {
                                                                        setSelectedReport(mapReportToEdit(report));
                                                                    }}>
                                                                    <PencilFill />
                                                                </Button>
                                                                <Button variant="danger" title={"Delete"} onClick={() => setDeleteReportId(report.report_id)}>
                                                                    <TrashFill />
                                                                </Button>
                                                            </ButtonGroup>
                                                        )}
                                                    </Card.Header>

                                                    <Card.Body>
                                                        <Card.Text>
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <Row className="gy-2">
                                                                    <FrequencyPicker
                                                                        formData={selectedReport}
                                                                        setFormData={setSelectedReport}
                                                                        name={moduleName + "edit"}
                                                                    />
                                                                    <WeeksPicker
                                                                        formData={selectedReport}
                                                                        setFormData={setSelectedReport}
                                                                        name={moduleName + "edit"}
                                                                    />
                                                                    <MinMaxSpinsPicker
                                                                        filterData={selectedReport}
                                                                        setFilterData={setSelectedReport}
                                                                        name={moduleName + "edit"}
                                                                    />
                                                                    <DaysPicker
                                                                        formData={selectedReport}
                                                                        setFormData={setSelectedReport}
                                                                        name={moduleName + "edit"}
                                                                    />

                                                                    <HoursPicker
                                                                        formData={selectedReport}
                                                                        setFormData={setSelectedReport}
                                                                        name={moduleName + "edit"}
                                                                        extraClass={"col-12 col-xl-12"}
                                                                    />
                                                                </Row>
                                                            ) : (
                                                                <div className="text-center">
                                                                    <div className="mb-1">
                                                                        <span>
                                                                            send every <strong>{report.frequency} </strong>weeks
                                                                        </span>
                                                                        <div className="vr  d-xl-inline-block mx-2"></div>
                                                                    </div>
                                                                    <div className="mb-1">
                                                                        <span>
                                                                            <strong>
                                                                                {report.min_spins} –{report.max_spins !== 0 ? report.max_spins : " ∞ "}
                                                                            </strong>
                                                                            spins
                                                                        </span>
                                                                        <div className="vr  d-xl-inline-block mx-2"></div>
                                                                        <span>
                                                                            <strong>{report.weeks_to_check}</strong> weeks
                                                                        </span>
                                                                    </div>
                                                                    <div className="mb-1">
                                                                        Perspective:{" "}
                                                                        <strong>
                                                                            {daysPerspectives.find(x => JSON.stringify(x.days) === JSON.stringify(report.days))
                                                                                ?.perspective ??
                                                                                `Custom - ${report.days
                                                                                    .map(d =>
                                                                                        moment()
                                                                                            .weekday((d + 1) % 7)
                                                                                            .format("dd")
                                                                                    )
                                                                                    .join(", ")}`}{" "}
                                                                        </strong>
                                                                        <div className="vr  d-xl-inline-block mx-2"></div>
                                                                    </div>
                                                                    <div className="mb-1">
                                                                        Time:{" "}
                                                                        <strong>
                                                                            {moment(report.start_time, "HH:mm").format("HH:mm")} -{" "}
                                                                            {moment(report.end_time, "HH:mm").format("HH:mm")}
                                                                        </strong>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Card.Text>
                                                        <Card.Text className="music-reports-email-card-edit text-center">
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <EmailAddressPicker
                                                                    formData={selectedReport}
                                                                    setFormData={setSelectedReport}
                                                                    name={moduleName + "edit"}
                                                                    inputOnly={true}
                                                                />
                                                            ) : (
                                                                <span className="rbt-token p-2">
                                                                    <span>{report.email_address.join(", ")}</span>
                                                                </span>
                                                            )}
                                                        </Card.Text>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="row justify-content-center">
                                            <div className="col-12">
                                                <span>No results.</span>
                                            </div>
                                        </div>
                                    )}
                                    <ConfirmPrompt
                                        show={deleteReportId !== null}
                                        handleClose={() => setDeleteReportId(null)}
                                        handleConfirm={async () => {
                                            await deleteMusicReport(deleteReportId);
                                            setDeleteReportId(null);
                                        }}
                                    />
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </Container>
    );
}
